/**
 * The sign-in surface, routed at `/sign-in` as `<page-sign-in>`.
 *
 * Implements `SC-F001` (email one-time code), `SC-F002` (this form is also the
 * sign-up form — the account, its personal organization and the owner
 * membership are created on the code *request*), `SC-F005` (usernameless
 * passkey login), `SC-F009` (the 24-hour session it mints) and `SC-F017` (the
 * rate limits it has to live inside). Stories: `SC-S001`, `SC-S002`, `SC-S003`,
 * `SC-S006`, `SC-S008`, `SC-S009`.
 *
 * **Two co-equal primaries, in a deliberate order.** `01` §1.1 is explicit that
 * a passkey login and an emailed code are co-equal primaries — a passkey is
 * UV-required and mints exactly the session an emailed code does, so it is not
 * a shortcut and the code is not a fallback. The email field is nonetheless
 * *first*, and invariant NL-1 is why: the emailed code is the one method every
 * account is guaranteed to retain, it is the only method that works for a
 * visitor who has no account yet (this page is the sign-up), and it is the only
 * one left when the authenticator is in a drawer at home. The passkey button is
 * a full-width peer immediately beside it, never a footnote, and it disappears
 * entirely — rather than failing later — on a browser or origin that cannot run
 * WebAuthn (`SC-S008`).
 *
 * **Conditional-mediation autofill is deliberately absent.** `SC-F006` is a v1
 * feature and `SC-S006`'s notes scope this story to the explicit button. Arming
 * a background ceremony would also mean owning the re-arm discipline, so the
 * email input asks for `autocomplete="email"` and not `"email webauthn"`: a
 * `webauthn` token with no ceremony armed offers the user a dropdown entry that
 * cannot work.
 *
 * **The four states.** A form is not a list, so they land like this: *loading*
 * is the session check on mount (an already-signed-in visitor never sees the
 * form) plus every in-flight submit; *empty* is the untouched email step;
 * *populated* is the code step, addressed to the address the code went to; and
 * *error* renders the `apierr` code's copy against the field that caused it,
 * with the `request_id` printed underneath so it can be quoted in a support
 * ticket.
 *
 * A throttle is not an error and is not rendered as one. `SC-F017` puts a
 * 5-per-hour budget on the code request; a user who presses Resend twice is
 * doing something reasonable, so the control counts down instead of failing.
 */

import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { RecordModel } from "pocketbase";

import "../components/index.js";
import { toast, type BnInput } from "../components/index.js";

import { loginWithPasskey, requestOtp, verifyOtp } from "../api/auth.js";
import { pb } from "../api/client.js";
import { parseError, supportReference } from "../api/errors.js";
import { passkeyFailureMessage, webauthnSupported } from "../api/webauthn.js";
import { navigate } from "../nav.js";

/**
 * Rejects only obviously malformed input, because the server is the authority
 * and a client-side address grammar that is stricter than the server's rejects
 * real mailboxes. Its whole job is to stop a typo from spending one of the five
 * hourly code requests.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * How long the page holds the send/resend control closed after a request, in
 * seconds.
 *
 * It is a *client* cooldown and the copy says only what it can honestly claim —
 * when this page will let the button be pressed again. It is not a prediction of
 * when the server's budget frees up: `users:requestOTP` is 5 per hour per IP
 * (`06` §7.2) over a window whose remaining time no response tells us, so a
 * countdown to the real reset would be invented.
 */
const RESEND_COOLDOWN_S = 60;

/**
 * Failed verifies of one OTP id before the page stops offering "verify" as a
 * path. `SC-S002` requires that the fourth attempt is refused and that
 * "send a new code" is then the only forward path, so the page stops asking the
 * user to spend an attempt that the throttle will refuse anyway.
 */
const MAX_CODE_ATTEMPTS = 3;

/**
 * Beyond this, an account with an empty display name is treated as one that
 * simply never set a name rather than a fresh sign-up. `SC-S001` makes the
 * onboarding detour conditional on *both* facts; the age test is what stops
 * `/welcome` becoming a toll booth on every sign-in.
 */
const FRESH_ACCOUNT_MS = 15 * 60_000;

@customElement("page-sign-in")
export class PageSignIn extends LitElement {
  /** Which half of the two-call ceremony the visitor is in. */
  @state() private step: "email" | "code" = "email";
  @state() private email = "";
  @state() private code = "";
  /** The id `request-otp` answered, paired with the code on `auth-with-otp`. */
  @state() private otpId = "";
  /**
   * Which request is in flight, or the empty string. It names the *action*
   * rather than being a boolean, because the code step has three buttons and a
   * shared flag would put the spinner on "Verify and sign in" when the reader
   * pressed "Send a new code".
   */
  @state() private busy: "" | "send" | "verify" | "resend" | "passkey" = "";
  /** True while the mount-time session check redirects an already-signed-in
   * visitor, so the form never flashes behind the navigation. */
  @state() private redirecting = false;

  /** Field-level failure copy, from the `apierr` code. */
  @state() private fieldError = "";
  /**
   * `request_id` of the failure above, rendered so it can be quoted — prefixed
   * with the `apierr` code.
   *
   * Both, and not just the id: the message beside the field is written for the
   * person reading it, while the CODE is what a support conversation and a log
   * search match on. This is the one screen an anonymous visitor can reach, so it
   * is also the one where "quote this line" has to be sufficient on its own.
   */
  @state() private errorRef = "";

  /** Seconds left on the send/resend cooldown; 0 when the control is open. */
  @state() private cooldown = 0;
  /** Why the cooldown is running, when it is running for a reason worth
   * stating (a 429). Empty for the ordinary post-send cooldown. */
  @state() private throttleNote = "";

  /** Failed verifies against the current [otpId]. See [MAX_CODE_ATTEMPTS]. */
  @state() private attempts = 0;

  /** Whether this browser and origin can run a WebAuthn ceremony at all.
   * Computed once — it cannot change while the page is mounted. */
  private readonly passkeySupport = webauthnSupported();

  private cooldownTimer = 0;
  private cooldownUntil = 0;

  /** True while any one-time-code request is in flight. The passkey ceremony is
   * excluded: it disables the code controls but is not one of them. */
  private get submitting(): boolean {
    return this.busy !== "" && this.busy !== "passkey";
  }

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: clamp(var(--bino-space-lg), 8vh, 6rem);
    }
    /* The surface itself is bn-card; this only sizes it. A second card
       implementation would be one more thing to keep in step with the kit. */
    .card {
      width: 100%;
      max-width: 25rem;
      font-family: var(--bino-font-sans);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    .lede {
      margin: 0 0 var(--bino-space-lg);
      font-size: var(--bino-font-size-md);
      line-height: 1.5;
      color: var(--bino-text-secondary);
    }
    .addr {
      color: var(--bino-text);
      font-weight: 500;
      word-break: break-all;
    }
    .code-input {
      /* Space the digits so a six-character code reads as six characters. The
         host inherits into bn-input's shadowed <input>. */
      letter-spacing: 0.35em;
    }
    .actions {
      margin-top: var(--bino-space-lg);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    /* The passkey path is a peer of the email path, not an afterthought: same
       width, same block, separated by a labelled rule rather than demoted below
       a fold. */
    .or {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin: var(--bino-space-xs) 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .or::before,
    .or::after {
      content: "";
      flex: 1;
      height: 1px;
      background: var(--bino-border);
    }
    .note {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      line-height: 1.5;
      color: var(--bino-text-secondary);
      text-align: center;
    }
    .status {
      margin: var(--bino-space-md) 0 0;
      padding: var(--bino-space-sm) var(--bino-space-md);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      font-size: var(--bino-font-size-sm);
      line-height: 1.5;
    }
    .ref {
      margin: var(--bino-space-xs) 0 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      /* One click selects the whole reference, which is what a reader pasting
         it into a support ticket is trying to do. */
      user-select: all;
    }
    .alt {
      margin-top: var(--bino-space-md);
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: var(--bino-space-xs) var(--bino-space-md);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // A valid stored session means there is nothing to sign in to. The guard
    // sends expired sessions here with `?next=`, so honour it rather than
    // dropping the visitor on the instance list.
    if (pb.authStore.isValid) {
      this.redirecting = true;
      this.redirectNext();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.clearCooldown();
  }

  override updated(changed: PropertyValues) {
    // Moving between the two halves of the ceremony moves focus with it, so a
    // keyboard user is never dropped at the top of a form they have already
    // half-completed.
    if (changed.has("step")) void this.focusField();
  }

  /**
   * focusField moves focus into the step's control. It awaits the control's own
   * `updateComplete` first: a step change swaps one bn-input for another, and
   * the replacement schedules its first render as a microtask — so reading its
   * shadow root synchronously from this element's `updated()` finds no <input>
   * at all and the focus silently does nothing.
   */
  private async focusField() {
    const host = this.renderRoot.querySelector<BnInput>(
      this.step === "email" ? "#email" : "#code",
    );
    if (!host) return;
    await host.updateComplete;
    // bn-input owns the real <input> inside its own shadow root.
    host.shadowRoot?.querySelector("input")?.focus();
  }

  // ───────────────────────────────────────────────────────── navigation ──

  /**
   * nextPath is the `?next=` destination, or the empty string when there is
   * none or when it is not a safe in-app path.
   *
   * The check is stricter than "starts with a slash" on purpose: `//evil.example`
   * and `/\evil.example` both start with one and both navigate **off-site**.
   * `?next=` is attacker-supplied — the guard puts it there, but so can any
   * link in any email — so this is an open-redirect guard, not tidiness.
   */
  private nextPath(): string {
    const raw = new URLSearchParams(location.search).get("next");
    if (!raw || raw[0] !== "/" || raw[1] === "/" || raw[1] === "\\") return "";
    return raw;
  }

  /** redirectNext lands on `?next=` when it is safe, otherwise on the instance
   * list — the product's home, because an instance is what a customer comes
   * here to operate (`SC-S001`). */
  private redirectNext() {
    navigate(this.nextPath() || "/instances", { replace: true });
  }

  /**
   * finishAuth is the single funnel every sign-in method lands in, so the
   * onboarding detour and the `?next=` redirect cannot drift between the code
   * path and the passkey path.
   *
   * `SC-S001` makes the detour conditional on *both* an empty display name and
   * an account minted in the last fifteen minutes. The name alone is not enough:
   * `SC-F002` creates the account on the code *request*, so every brand-new
   * account has an empty name — but so does a two-year-old account whose owner
   * skipped onboarding, and sending that person to `/welcome` on every sign-in
   * would be a toll booth.
   */
  private finishAuth(record: RecordModel | null) {
    toast("Signed in", "success");
    const name = typeof record?.name === "string" ? record.name.trim() : "";
    // The auth substrate stamps `created` with a space separator; Date.parse
    // needs the ISO "T".
    const createdMs = record?.created ? Date.parse(String(record.created).replace(" ", "T")) : NaN;
    const fresh = Number.isFinite(createdMs) && Date.now() - createdMs < FRESH_ACCOUNT_MS;
    if (name === "" && fresh) {
      const next = this.nextPath();
      navigate("/welcome" + (next ? "?next=" + encodeURIComponent(next) : ""), { replace: true });
      return;
    }
    this.redirectNext();
  }

  // ────────────────────────────────────────────────────────── cooldown ──

  /** startCooldown closes the send/resend control for `seconds` and ticks the
   * visible countdown. `note` explains the wait when there is something worth
   * explaining; the ordinary post-send cooldown passes none. */
  private startCooldown(seconds: number, note = "") {
    this.clearCooldown();
    this.throttleNote = note;
    this.cooldownUntil = Date.now() + seconds * 1000;
    this.cooldown = seconds;
    this.cooldownTimer = window.setInterval(() => {
      const left = Math.ceil((this.cooldownUntil - Date.now()) / 1000);
      this.cooldown = left > 0 ? left : 0;
      if (left <= 0) {
        window.clearInterval(this.cooldownTimer);
        this.cooldownTimer = 0;
        this.throttleNote = "";
      }
    }, 250);
  }

  private clearCooldown() {
    if (this.cooldownTimer) window.clearInterval(this.cooldownTimer);
    this.cooldownTimer = 0;
    this.cooldown = 0;
  }

  /** countdown renders the remaining wait as `m:ss` past a minute and as bare
   * seconds below it, because "0:07" reads as a stopwatch and "7 seconds" reads
   * as an answer. */
  private countdown(): string {
    const s = this.cooldown;
    if (s >= 60) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    return `${s} second${s === 1 ? "" : "s"}`;
  }

  // ───────────────────────────────────────────────────────────── errors ──

  /**
   * failed renders a failure against the active field. A `rate_limited` never
   * lands here: it is not a mistake the user made and it has a different shape
   * of remedy, so [throttled] takes it instead.
   */
  private failed(err: unknown) {
    const e = parseError(err);
    this.fieldError = e.message;
    this.errorRef = reference(e);
  }

  /** throttled converts a 429 into a visible wait rather than a red field. The
   * budget is real (`SC-F017`), the user's intent was reasonable, and a
   * countdown tells them what to do where an error only tells them to feel bad
   * about it. */
  private throttled(err: unknown) {
    const e = parseError(err);
    this.fieldError = "";
    this.errorRef = reference(e);
    this.startCooldown(
      RESEND_COOLDOWN_S,
      "Sluice Cloud limits how many sign-in codes one network can request per hour. " +
        "Any code already in your inbox still works.",
    );
  }

  private clearFeedback() {
    this.fieldError = "";
    this.errorRef = "";
  }

  // ──────────────────────────────────────────────────────── ceremonies ──

  /**
   * sendCode runs the first half of the ceremony. It is also the sign-up:
   * `SC-F002` creates the account, its personal organization and the owner
   * membership inside this call for an address that has none, which is why the
   * copy promises a code rather than "an account if you have one".
   */
  private async sendCode() {
    const email = this.email.trim();
    if (!EMAIL_RE.test(email)) {
      this.fieldError = "Enter an email address, for example you@company.example.";
      this.errorRef = "";
      return;
    }
    this.clearFeedback();
    this.busy = "send";
    try {
      const { otpId } = await requestOtp(email);
      this.otpId = otpId;
      this.code = "";
      this.attempts = 0;
      this.step = "code";
      this.startCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      if (parseError(err).code === "rate_limited") this.throttled(err);
      else this.failed(err);
    } finally {
      this.busy = "";
    }
  }

  /**
   * verify redeems the code. There is no expired-versus-wrong branch and there
   * must not be one: the auth substrate answers wrong code, expired code and
   * unknown otpId with the identical opaque 400 so the route is not an
   * account-existence oracle (`SC-S002`), and `api/auth.ts` re-tags it
   * `otp_invalid`. Inventing three messages here would undo that.
   */
  private async verify() {
    const code = this.code.trim();
    if (code === "") {
      this.fieldError = "Enter the code from your email.";
      this.errorRef = "";
      return;
    }
    this.clearFeedback();
    this.busy = "verify";
    try {
      await verifyOtp(this.otpId, code);
      this.finishAuth(pb.authStore.record);
    } catch (err) {
      if (parseError(err).code === "rate_limited") {
        this.throttled(err);
        this.attempts = MAX_CODE_ATTEMPTS;
      } else {
        this.failed(err);
        this.attempts += 1;
      }
    } finally {
      this.busy = "";
    }
  }

  /** resend asks for a fresh code for the same address, replacing the otpId and
   * resetting the attempt count with it. */
  private async resend() {
    if (this.cooldown > 0 || this.submitting) return;
    this.clearFeedback();
    this.busy = "resend";
    try {
      const { otpId } = await requestOtp(this.email.trim());
      this.otpId = otpId;
      this.code = "";
      this.attempts = 0;
      this.startCooldown(RESEND_COOLDOWN_S);
      toast("A new code is on its way.", "success");
      void this.focusField();
    } catch (err) {
      if (parseError(err).code === "rate_limited") this.throttled(err);
      else this.failed(err);
    } finally {
      this.busy = "";
    }
  }

  /**
   * passkeySignIn runs the discoverable-credential ceremony. No email is typed
   * and none is needed: the authenticator names the account, and the assertion
   * is UV-required, so what comes back is a complete sign-in and not a second
   * factor (`SC-S006`).
   */
  private async passkeySignIn() {
    if (this.busy !== "") return;
    this.clearFeedback();
    this.busy = "passkey";
    try {
      const record = await loginWithPasskey();
      this.finishAuth(record);
    } catch (err) {
      // Dismissing the platform dialog is a decision, not a failure, and
      // passkeyFailureMessage returns null for exactly that case. Everything
      // else — an origin whose RP id does not match, a server rejection —
      // is named, and the email path beside it stays usable either way.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
    } finally {
      this.busy = "";
    }
  }

  private onKey(e: KeyboardEvent, action: () => void) {
    if (e.key === "Enter" && !this.submitting) {
      e.preventDefault();
      action();
    }
  }

  // ───────────────────────────────────────────────────────────── render ──

  override render() {
    if (this.redirecting) {
      return html`<bn-spinner block size="28" label="Signing you in"></bn-spinner>`;
    }
    return html`<bn-card class="card">
      ${this.step === "email" ? this.renderEmailStep() : this.renderCodeStep()}
    </bn-card>`;
  }

  private renderEmailStep() {
    const busy = this.busy !== "";
    const waiting = this.cooldown > 0;
    return html`
      <h1>Sign in to Sluice Cloud</h1>
      <p class="lede">
        There is no password. Enter your email address and we will send you a six-digit code — if you
        have never been here before, that same step creates your account and your organization.
      </p>

      <bn-field
        label="Email address"
        required
        error=${this.fieldError}
        hint="We send a new code every time. Nothing to remember."
      >
        <bn-input
          id="email"
          type="email"
          inputmode="email"
          autocomplete="email"
          placeholder="you@company.example"
          ?invalid=${this.fieldError !== ""}
          ?disabled=${busy}
          .value=${this.email}
          @input=${(e: CustomEvent<{ value: string }>) => {
            this.email = e.detail.value;
            if (this.fieldError) this.clearFeedback();
          }}
          @keydown=${(e: KeyboardEvent) => this.onKey(e, () => void this.sendCode())}
        ></bn-input>
      </bn-field>
      ${this.errorRef && this.fieldError ? html`<p class="ref">${this.errorRef}</p>` : nothing}

      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.busy === "send"}
          ?disabled=${this.busy === "passkey" || waiting}
          @click=${() => void this.sendCode()}
        >
          ${waiting ? html`Try again in ${this.countdown()}` : html`Email me a code`}
        </bn-button>
        ${this.passkeySupport
          ? html`
              <div class="or">or</div>
              <bn-button
                variant="secondary"
                block
                ?loading=${this.busy === "passkey"}
                ?disabled=${this.submitting}
                @click=${() => void this.passkeySignIn()}
              >
                Use a passkey
              </bn-button>
              <p class="note">
                A passkey signs you in with your fingerprint, face or device PIN. No address to type,
                and it is a complete sign-in — not a second step.
              </p>
            `
          : nothing}
      </div>

      ${this.renderThrottleNote()}
    `;
  }

  private renderCodeStep() {
    const spent = this.attempts >= MAX_CODE_ATTEMPTS;
    const waiting = this.cooldown > 0;
    return html`
      <h1>Check your email</h1>
      <p class="lede">
        We sent a six-digit code to <span class="addr">${this.email.trim()}</span>. It is good for a
        few minutes.
      </p>

      <bn-field
        label="Six-digit code"
        required
        error=${spent
          ? "Too many attempts with this code. Request a new one to continue."
          : this.fieldError}
      >
        <bn-input
          id="code"
          class="code-input"
          mono
          inputmode="numeric"
          autocomplete="one-time-code"
          placeholder="123456"
          ?invalid=${this.fieldError !== "" || spent}
          ?disabled=${this.submitting || spent}
          .value=${this.code}
          @input=${(e: CustomEvent<{ value: string }>) => {
            this.code = e.detail.value;
            if (this.fieldError) this.clearFeedback();
          }}
          @keydown=${(e: KeyboardEvent) => this.onKey(e, () => void this.verify())}
        ></bn-input>
      </bn-field>
      ${this.errorRef && (this.fieldError || spent)
        ? html`<p class="ref">${this.errorRef}</p>`
        : nothing}

      <div class="actions">
        ${spent
          ? nothing
          : html`<bn-button
              variant="primary"
              block
              ?loading=${this.busy === "verify"}
              @click=${() => void this.verify()}
            >
              Verify and sign in
            </bn-button>`}
        <bn-button
          variant=${spent ? "primary" : "secondary"}
          block
          ?loading=${this.busy === "resend"}
          ?disabled=${waiting || this.submitting}
          @click=${() => void this.resend()}
        >
          ${waiting ? html`New code available in ${this.countdown()}` : html`Send a new code`}
        </bn-button>
      </div>

      ${this.renderThrottleNote()}

      <div class="alt">
        <bn-button
          variant="ghost"
          size="sm"
          ?disabled=${this.submitting}
          @click=${() => this.backToEmail()}
        >
          Use a different address
        </bn-button>
      </div>
    `;
  }

  /**
   * The throttle notice. `role="status"` rather than `role="alert"`: a wait is
   * information, and interrupting a screen-reader user to announce a countdown
   * as an emergency is the audible version of rendering it in red.
   */
  private renderThrottleNote() {
    if (!this.throttleNote) return nothing;
    return html`<p class="status" role="status">
      ${this.throttleNote} You can try again in ${this.countdown()}.
      ${this.errorRef ? html`<br /><span class="ref">${this.errorRef}</span>` : nothing}
    </p>`;
  }

  private backToEmail() {
    this.step = "email";
    this.otpId = "";
    this.code = "";
    this.attempts = 0;
    this.clearFeedback();
  }
}

/**
 * reference is the one line a reader quotes: the `apierr` code, then the
 * `request_id` when the request reached the server.
 *
 * [supportReference] alone answers the id and the empty string, which is the right
 * shape for a page that renders the code elsewhere. This page does not — its
 * failures land as field copy — so a bare id would leave the code, the half a log
 * search matches on, nowhere on screen. A failure that never reached Sluice Cloud
 * still yields its code, so this is never empty for a server-shaped error.
 */
function reference(err: unknown): string {
  const e = parseError(err);
  return [e.code, supportReference(e)].filter((s) => s !== "").join(" · ");
}

declare global {
  interface HTMLElementTagNameMap {
    "page-sign-in": PageSignIn;
  }
}
